import { Injectable, signal, computed, inject } from '@angular/core';
import { IBizEntity, BizEntityTypeCategory } from '@optima-stride-ai/core';
import {
  IExplorerAttributes,
  IExplorerHeaderAttributes,
  IExplorerNavAttributes,
  IExplorerContentAttributes,
  ContextMenuItem
} from '@optima-stride-ai/optima-components';
import { filterBizEntitiesForApplicationExplorer } from '@optima-stride-ai/optima-app-framework';
import { LibraryDataTransformerService } from './library-data-transformer.service';
import { LibraryFacadeService } from './library-facade.service';
import { BizEntityConstants } from '../../../shared/enums/app-constants';
import { GlobalStateService } from '../../../../service/global.service';

/**
 * Service responsible for managing Explorer state and attributes
 * Centralizes all Explorer-related logic and data transformations
 */
@Injectable({
  providedIn: 'root'
})
export class LibraryExplorerStateService {
  private readonly dataTransformer = inject(LibraryDataTransformerService);
  private readonly facade = inject(LibraryFacadeService);
  private readonly global = inject(GlobalStateService);

  // Explorer state
  explorerAttributes = signal<IExplorerAttributes>({
    data: [],
    isInModal: false,
    preSelectedItemId: null,
    subHeaderAttributes: {
      search: true,
      filter: true,
      add: true,
      view: true,
      sideBar: true,
      addWithOptions: false,
      breadCrumbToggle: true,
      nextDisable: false,
      previouseDisable: false,
      appliedFilterCount: 0,
      graphView: false,
      searchResults: [],
      activeMenuItem: null,
      maxVisibleBreadcrumbs: 3,
    },
    explorerNavAttributes: {
      data: [],
      enableDragDrop: true,
    },
    explorerContentAttributes: {
      data: [],
      listData: {
        title: '',
        colHeaders: [],
        items: []
      },
      isApplicationDerivedContent: false
    },
    contextMenuItems: []
  });

  dataRefreshedToken = signal<string>('');
  selectedParentId = signal<string>('');

  // Available folders for upload (computed from library data)
  availableFolders = computed(() => {
    const data = this.explorerAttributes().data || [];
    return data.filter(item =>
      item.type?.isAlwaysNavigable === true &&
      item.parentId === BizEntityConstants.ORGANIZATIONS_LIB_ID
    );
  });

  /**
   * Initialize explorer with library data
   */
  async initializeExplorer(
    libraryData: IBizEntity[],
    contextMenuGetter: (item?: any) => ContextMenuItem[],
    onContextMenu: (info: any) => void,
    onItemSelected: (itemId: string) => void,
    onListItemClicked: (itemId: string) => void,
    onOpenInViewer: (entity: IBizEntity) => void,
    itemParam?: string,
    syncGlobalSelection: boolean = true,
    initialContent?: IBizEntity[],
    knownEmptyFolderIds?: string[]
  ): Promise<void> {
    // Filter out excluded categories before processing
    const filteredLibraryData = filterBizEntitiesForApplicationExplorer(libraryData);

    const subHeaderAttributes: IExplorerHeaderAttributes = {
      search: true,
      filter: true,
      add: true,
      view: true,
      sideBar: true,
      addWithOptions: false,
      addButtonOptions: [],
      breadCrumbToggle: true,
      nextDisable: false,
      previouseDisable: false,
      appliedFilterCount: 0,
      graphView: false,
      searchResults: [],
      activeMenuItem: null,
            maxVisibleBreadcrumbs: 3,
    };

    const { firstItem, preSelectedItemId } = this.getInitialSelection(filteredLibraryData, itemParam);
    this.selectedParentId.set(preSelectedItemId);
    if (syncGlobalSelection) {
      this.global.setSelectedBizEntityId(preSelectedItemId);
    }
    const explorerNavAttributes: IExplorerNavAttributes = {
      data: filteredLibraryData,
      currentSelectedItem: preSelectedItemId,
      isNavigable: false,
      enableDragDrop: true,
      knownEmptyFolderIds,
    };

    // Store navigation attributes in facade
    this.facade.setOriginalNavAttributes(explorerNavAttributes);
    const firstItemChildren = filterBizEntitiesForApplicationExplorer(
      initialContent ?? filteredLibraryData.filter(item => item.parentId == preSelectedItemId)
    );
    const listData = await this.dataTransformer.buildListView(
      firstItemChildren,
      onContextMenu,
      onItemSelected,
      onListItemClicked,
      onOpenInViewer
    );

    const explorerContentAttributes: IExplorerContentAttributes = {
      data: firstItemChildren,
      listData: listData,
      currentSelectedItem: preSelectedItemId,
      isInModal: false,
      isApplicationDerivedContent: false
    };

    this.dataRefreshedToken.set(this.dataTransformer.generateUUID());
    
    this.explorerAttributes.set({
      preSelectedItemId: preSelectedItemId,
      data: filteredLibraryData,
      isInModal: false,
      subHeaderAttributes: subHeaderAttributes,
      explorerNavAttributes: explorerNavAttributes,
      explorerContentAttributes: explorerContentAttributes,
      contextMenuItems: contextMenuGetter(firstItem)
    });
  }

  /**
   * Initialize explorer with category-specific data (for direct category route access)
   * This method sets up navigation with full library structure but displays category-filtered content
   */
  async initializeExplorerWithCategoryData(
    libraryData: IBizEntity[],
    categoryData: IBizEntity[],
    parentId: string,
    contextMenuGetter: (item?: any) => ContextMenuItem[],
    onContextMenu: (info: any) => void,
    onItemSelected: (itemId: string) => void,
    onListItemClicked: (itemId: string) => void,
    onOpenInViewer: (entity: IBizEntity) => void,
    syncGlobalSelection: boolean = true
  ): Promise<void> {
    // Filter out excluded categories before processing
    const filteredLibraryData = filterBizEntitiesForApplicationExplorer(libraryData);
    const filteredCategoryData = filterBizEntitiesForApplicationExplorer(categoryData);

    const subHeaderAttributes: IExplorerHeaderAttributes = {
      search: true,
      filter: true,
      add: true,
      view: true,
      sideBar: true,
      addWithOptions: false,
      addButtonOptions: [],
      breadCrumbToggle: true,
      nextDisable: false,
      previouseDisable: false,
      appliedFilterCount: 0,
      graphView: false,
      searchResults: [],
      activeMenuItem: null,
            maxVisibleBreadcrumbs: 3,
    };
    const { preSelectedItemId } = this.getInitialSelection(filteredLibraryData);
    // Preserve caller context (current selected folder) when provided; fallback
    // to default initial selection only when parentId is missing/invalid.
    const hasRequestedParent = !!parentId && filteredLibraryData.some((item) => item.id === parentId);
    const selectedParentId = hasRequestedParent ? parentId : preSelectedItemId;
    this.selectedParentId.set(selectedParentId);
    if (syncGlobalSelection) {
      this.global.setSelectedBizEntityId(preSelectedItemId);
    }

    // Navigation uses full library data for hierarchy
    const explorerNavAttributes: IExplorerNavAttributes = {
      data: filteredLibraryData,
      currentSelectedItem: preSelectedItemId,
      isNavigable: false,
      isLoading: false,
    };
    // Store navigation attributes in facade
    this.facade.setOriginalNavAttributes(explorerNavAttributes);

    // Content area uses category-filtered data
    this.setSelectedParentId(preSelectedItemId);
    if (syncGlobalSelection) {
      this.global.setSelectedBizEntityId(preSelectedItemId);
    }
    
    const listData = await this.dataTransformer.buildListView(
      filteredCategoryData,
      onContextMenu,
      onItemSelected,
      onListItemClicked,
      onOpenInViewer
    );

    const explorerContentAttributes: IExplorerContentAttributes = {
      data: filteredCategoryData,
      listData: listData,
      currentSelectedItem: preSelectedItemId,
      isInModal: false,
      isApplicationDerivedContent: false
    };

    this.dataRefreshedToken.set(this.dataTransformer.generateUUID());
    
    // Set attributes with navigation showing full hierarchy and content showing filtered data
    this.explorerAttributes.set({
      preSelectedItemId: preSelectedItemId,
      data: filteredLibraryData,
      isInModal: false,
      subHeaderAttributes: subHeaderAttributes,
      explorerNavAttributes: explorerNavAttributes,
      explorerContentAttributes: explorerContentAttributes,
      contextMenuItems: contextMenuGetter()
    });
  }

  /**
   * Update explorer with children data
   */
  async updateExplorerWithChildren(
    itemId: string,
    children: IBizEntity[],
    onContextMenu: (info: any) => void,
    onItemSelected: (itemId: string) => void,
    onListItemClicked: (itemId: string) => void,
    onOpenInViewer: (entity: IBizEntity) => void
  ): Promise<void> {
    // Filter out excluded categories before processing
    const filteredChildren = filterBizEntitiesForApplicationExplorer(children);

    const currentData = this.explorerAttributes().data || [];
    const selectedItem = currentData.find(item => item.id === itemId);
    const attrs = this.explorerAttributes();

    // Check if we're in viewer mode - only allow updates for navigable items
    if (attrs.explorerContentAttributes?.isApplicationDerivedContent) {
      if (
        selectedItem?.type?.category === BizEntityTypeCategory.HayagrivaLibrary ||
        selectedItem?.type?.category === BizEntityTypeCategory.HayagrivaLibFolder ||
        selectedItem?.type?.category === BizEntityTypeCategory.Tenant ||
        selectedItem?.type?.isAlwaysNavigable === true
      ) {
        // Continue with update
      } else {
        return;
      }
    }

    // if (selectedItem?.type?.functionType === BizEntityFunctionTypes.Operational) {
    //   console.warn('Operational entities are not navigable. Update aborted.');
    //   return;
    // }

    // Build view data
    const displayItems = filteredChildren.length > 0 ? filteredChildren : [];
    const normalizedItems = this.dataTransformer.normalizeEntityData(displayItems);
    const contentListData = await this.dataTransformer.buildListView(
      normalizedItems,
      onContextMenu,
      onItemSelected,
      onListItemClicked,
      onOpenInViewer
    );

    const currentAttrs = this.explorerAttributes();
    const wasInViewerMode = currentAttrs.explorerContentAttributes?.isApplicationDerivedContent === true;
    const currentNavData = currentAttrs.explorerNavAttributes?.data || currentAttrs.data || [];
    const knownNavIds = new Set(currentNavData.map((item) => item.id));
    const updatedNavData = [
      ...currentNavData,
      ...filteredChildren.filter((item) => !knownNavIds.has(item.id)),
    ];

    const shouldKeepViewerOpen =
      wasInViewerMode &&
      selectedItem?.type?.category !== BizEntityTypeCategory.HayagrivaLibrary &&
      selectedItem?.type?.category !== BizEntityTypeCategory.HayagrivaLibFolder &&
      selectedItem?.type.category !== BizEntityTypeCategory.Tenant;

    const updatedAttrs: IExplorerAttributes = {
      ...currentAttrs,
      preSelectedItemId: null,
      data: updatedNavData,
      explorerNavAttributes: {
        ...currentAttrs.explorerNavAttributes!,
        data: updatedNavData,
        currentSelectedItem: itemId,
             enableDragDrop: true,
             isLoading: false
      },
      explorerContentAttributes: {
        ...currentAttrs.explorerContentAttributes!,
        data: normalizedItems,
        listData: contentListData,
        currentSelectedItem: itemId,
        isApplicationDerivedContent: shouldKeepViewerOpen ? true : false,
        isInModal: false
      },
    };

    this.dataRefreshedToken.set(this.dataTransformer.generateUUID());
    this.explorerAttributes.set(updatedAttrs);
  }

  updateNavigationWithChildren(itemId: string, children: IBizEntity[], knownEmptyFolderIds?: string[]): void {
    const filteredChildren = filterBizEntitiesForApplicationExplorer(children);
    const currentAttrs = this.explorerAttributes();
    const currentNavData = currentAttrs.explorerNavAttributes?.data || currentAttrs.data || [];
    const knownIds = new Set(currentNavData.map((item) => item.id));
    const navigationData = [
      ...currentNavData,
      ...filteredChildren.filter((item) => !knownIds.has(item.id)),
    ];
    const mergedEmptyFolderIds = knownEmptyFolderIds?.length
      ? [...new Set([...(currentAttrs.explorerNavAttributes?.knownEmptyFolderIds || []), ...knownEmptyFolderIds])]
      : currentAttrs.explorerNavAttributes?.knownEmptyFolderIds;

    this.explorerAttributes.set({
      ...currentAttrs,
      data: navigationData,
      explorerNavAttributes: {
        ...currentAttrs.explorerNavAttributes!,
        data: navigationData,
        currentSelectedItem: currentAttrs.explorerNavAttributes?.currentSelectedItem || itemId,
        knownEmptyFolderIds: mergedEmptyFolderIds,
      },
    });
    this.dataRefreshedToken.set(this.dataTransformer.generateUUID());
  }

  /**
   * Update explorer after file upload
   */
  async updateAfterUpload(
    folderId: string,
    folderChildren: IBizEntity[],
    onContextMenu: (info: any) => void,
    onItemSelected: (itemId: string) => void,
    onListItemClicked: (itemId: string) => void,
    onOpenInViewer: (entity: IBizEntity) => void
  ): Promise<void> {
    // Filter out excluded categories before processing
    const filteredFolderChildren = filterBizEntitiesForApplicationExplorer(folderChildren);

    const contentListData = await this.dataTransformer.buildListView(
      filteredFolderChildren,
      onContextMenu,
      onItemSelected,
      onListItemClicked,
      onOpenInViewer
    );

    const currentAttrs = this.explorerAttributes();
    const currentData = currentAttrs.data || [];

    const newAttrs: IExplorerAttributes = {
      ...currentAttrs,
      preSelectedItemId: null,
      data: currentData,
      explorerNavAttributes: {
        ...currentAttrs.explorerNavAttributes!,
        data: currentData,
        currentSelectedItem: folderId,
             enableDragDrop: true,
      },
      explorerContentAttributes: {
        ...currentAttrs.explorerContentAttributes!,
        data: filteredFolderChildren,
        listData: contentListData,
        currentSelectedItem: folderId,
        isInModal: false
      }
    };

    this.dataRefreshedToken.set(this.dataTransformer.generateUUID());
    this.explorerAttributes.set(newAttrs);
  }
  /**
   * Refresh explorer data in-place without triggering preSelectedItemId navigation.
   *
   * Use this instead of re-initializing the explorer when the user is already
   * inside a folder and only the data needs to be updated (e.g. after entity
   * creation/deletion). Setting preSelectedItemId would cause the Explorer to
   * call navigateToPreSelectedItem(), which navigates to the PARENT of the
   * current folder — firing onItemSelected() with the wrong (parent) id.
   */
  async refreshInPlace(
    allData: IBizEntity[],
    currentFolderId: string,
    currentFolderChildren: IBizEntity[],
    onContextMenu: (info: any) => void,
    onItemSelected: (itemId: string) => void,
    onListItemClicked: (itemId: string) => void,
    onOpenInViewer: (entity: IBizEntity) => void
  ): Promise<void> {
    const filteredAllData = filterBizEntitiesForApplicationExplorer(allData);
    const filteredChildren = filterBizEntitiesForApplicationExplorer(currentFolderChildren);

    const contentListData = await this.dataTransformer.buildListView(
      filteredChildren,
      onContextMenu,
      onItemSelected,
      onListItemClicked,
      onOpenInViewer
    );

    const currentAttrs = this.explorerAttributes();
    this.explorerAttributes.set({
      ...currentAttrs,
      data: filteredAllData,
      preSelectedItemId: null,
      explorerNavAttributes: {
        ...currentAttrs.explorerNavAttributes!,
        data: filteredAllData,
        currentSelectedItem: currentFolderId,
        enableDragDrop: true,
      },
      explorerContentAttributes: {
        ...currentAttrs.explorerContentAttributes!,
        data: filteredChildren,
        listData: contentListData,
        currentSelectedItem: currentFolderId,
        isApplicationDerivedContent: false,
        isInModal: false
      }
    });

    this.selectedParentId.set(currentFolderId);
    this.global.setSelectedBizEntityId(currentFolderId);
    this.dataRefreshedToken.set(this.dataTransformer.generateUUID());
  }

  /**
   * Refresh explorer data
   */
  refreshExplorer(): void {
    this.dataRefreshedToken.set(this.dataTransformer.generateUUID());
  }

  /**
   * Replace just the navigation tree data while preserving every other
   * facet of the current explorer state (content area, selection, header
   * attributes, etc.). Used by category-specific routes to keep the nav
   * hierarchy pruned to the ancestors of the currently visible items
   * without disturbing the content pane the user is looking at.
   *
   * Callers MUST trigger `refreshExplorer()` separately if they need the
   * underlying nav component to re-render; this method only mutates the
   * signal.
   */
  setNavData(navData: IBizEntity[]): void {
    // Filter out excluded categories before setting navigation data
    const filteredNavData = filterBizEntitiesForApplicationExplorer(navData);

    const currentAttrs = this.explorerAttributes();
    this.explorerAttributes.set({
      ...currentAttrs,
      data: filteredNavData,
      explorerNavAttributes: {
        ...(currentAttrs.explorerNavAttributes ?? { enableDragDrop: true }),
        data: filteredNavData,
      },
    });
  }

  /**
   * Resolve initial explorer selection with this precedence:
   * explicit item param -> tenant -> application root -> first item.
   */
  private getInitialSelection(
    libraryData: IBizEntity[],
    itemParam?: string
  ): { firstItem?: IBizEntity; preSelectedItemId: string } {
    const firstItem = libraryData.find(
      (item: IBizEntity) => item.type?.category === BizEntityTypeCategory.ApplicationRootNode
    );
    const tenantItem = libraryData.find(
      (item: IBizEntity) => item.type?.category === BizEntityTypeCategory.Tenant
    );
    const firstItemId = firstItem?.id || (libraryData.length > 0 ? libraryData[0].id : '');
    const defaultSelection = tenantItem?.id || firstItemId;

    let preSelectedItemId = defaultSelection;

    if (itemParam) {
      const selectedEntity = libraryData.find((item: IBizEntity) => item.id?.toString() === itemParam.toString());

      if (!selectedEntity) {
        preSelectedItemId = itemParam;
      } else if (selectedEntity.type?.isAlwaysNavigable === true || selectedEntity.type?.category === BizEntityTypeCategory.Tenant) {
        preSelectedItemId = selectedEntity.id;
      } else {
        preSelectedItemId = selectedEntity.parentId || defaultSelection;
      }
    }

    return { firstItem, preSelectedItemId };
  }

  /**
   * Reset explorer state to a clean empty baseline.
   *
   * Required because this service is `providedIn: 'root'` and its signals
   * persist across libraries-page component instances. Each `/documents/*`
   * route reuses the same component file but routes Angular to recreate
   * the component on navigation — meaning the new instance's FIRST template
   * render uses the previous route's stale signal data. Calling this from
   * the new component's `ngOnInit` (before async data loading) ensures the
   * Explorer mounts with empty state instead of inheriting the prior route.
   *
   * Also needed because the underlying `<app-explorer-nav>` early-returns
   * inside `prepareData()` when given empty data, leaving any previously
   * built `hierarchyNodes` intact — i.e. once a stale hierarchy is rendered
   * it cannot be cleared by simply pushing empty data later. Resetting
   * BEFORE the Explorer's nav child is instantiated avoids that trap.
   */
  resetExplorerState(): void {
    this.selectedParentId.set('');
    this.explorerAttributes.set({
      data: [],
      isInModal: false,
      preSelectedItemId: null,
      subHeaderAttributes: {
        search: true,
        filter: true,
        add: true,
        view: true,
        sideBar: true,
        addWithOptions: false,
        breadCrumbToggle: true,
        nextDisable: false,
        previouseDisable: false,
        appliedFilterCount: 0,
        graphView: false,
        searchResults: [],
        activeMenuItem: null,
              maxVisibleBreadcrumbs: 3,
      },
      explorerNavAttributes: {
        data: [],
        enableDragDrop: true,
      },
      explorerContentAttributes: {
        data: [],
        listData: {
          title: '',
          colHeaders: [],
          items: []
        },
        isApplicationDerivedContent: false
      },
      contextMenuItems: []
    });
    this.dataRefreshedToken.set(this.dataTransformer.generateUUID());
  }

  /**
   * Get current selected parent ID
   */
  getSelectedParentId(): string {
    return this.selectedParentId();
  }

  /**
   * Set selected parent ID
   */
  setSelectedParentId(id: string): void {
    this.selectedParentId.set(id);
  }
}
